import React, { useEffect, useState, useMemo } from 'react';
import { Piece, PieceName, SheetContainer, SheetWrap } from './EstimatesStyles';

// Define a function to generate distinct colors for each piece group
const getPieceColor = (name, index) => {
  const colors = [
    'rgba(0, 128, 255, 0.5)', // Blue
    'rgba(255, 165, 0, 0.5)',  // Orange
    'rgba(0, 128, 0, 0.5)',   // Green
    'rgba(255, 0, 0, 0.5)',    // Red
    'rgba(128, 0, 128, 0.5)', // Purple
    'rgba(0, 255, 255, 0.5)', // Cyan
    'rgba(255, 255, 0, 0.5)', // Yellow
  ];
  return colors[index % colors.length]; // Cycle through colors based on index
};

const Sheet = ({
  versions,
  totalsheetwidth,
  totalsheetlength,
  tail,
  colorbar,
  colorbarbuff,
  gripper,
  bleed,
  length,
  width,
  setNeededsheets,
  layout,
  setLayout,
}) => {
  const [items, setItems] = useState([]);

  const scaleup = (num) => Number(num) * 10;
  const setnewwidth = (width) => parseFloat(width) + parseFloat(bleed) * 2;
  const setnewlength = (length) => parseFloat(length) + parseFloat(bleed) * 2;

  const SHEET_WIDTH = Number(totalsheetwidth) * 10;
  const SHEET_HEIGHT = Number(totalsheetlength) * 10;

  const getItems = () => {
    const newItems = versions?.flatMap((item, index) =>
      Array.from({ length: Number(item.up) || 0 }, (_, upIndex) => ({
        id: `${index}-${upIndex}`,
        name: item.name,
        width: scaleup(setnewwidth(item.width)),
        height: scaleup(setnewlength(item.length)),
        up: Number(item.up) || 0,
        originalwidth: item.width,
        originallength: item.length,
        color: getPieceColor(item.name, index), // Assign color based on version index
      }))
    );
    setItems(newItems);
  };

  useEffect(() => {
    getItems();
  }, [JSON.stringify(versions), bleed]);

  const [newWidth, setNewWidth] = useState('');
  const [newHeight, setNewHeight] = useState('');

  const packPieces = (SHEET_WIDTH, SHEET_HEIGHT, pieces) => {
    const sortedPieces = [...pieces]; // Maintain input order
    const placed = [];
    const unplaced = [];
    let currentX = 0;
    let currentY = 0;
    let currentRowHeight = 0;

    // First Pass: Try to place all pieces in original orientation
    sortedPieces.forEach(piece => {
      const originalOrientation = {
        width: piece.width,
        height: piece.height,
        rotated: false,
      };

      // Check if the piece fits in the current row
      if (
        originalOrientation.width <= SHEET_WIDTH - currentX &&
        originalOrientation.height <= SHEET_HEIGHT - currentY &&
        originalOrientation.width <= SHEET_WIDTH &&
        originalOrientation.height <= SHEET_HEIGHT
      ) {
        // Place the piece
        placed.push({
          ...piece,
          x: currentX,
          y: currentY,
          width: originalOrientation.width,
          height: originalOrientation.height,
          rotated: originalOrientation.rotated,
        });

        // Update currentX and row height
        currentX += originalOrientation.width;
        currentRowHeight = Math.max(currentRowHeight, originalOrientation.height);
      } else {
        // Try starting a new row
        currentY += currentRowHeight;
        currentX = 0;
        currentRowHeight = 0;

        if (
          originalOrientation.width <= SHEET_WIDTH - currentX &&
          originalOrientation.height <= SHEET_HEIGHT - currentY &&
          originalOrientation.width <= SHEET_WIDTH &&
          originalOrientation.height <= SHEET_HEIGHT
        ) {
          placed.push({
            ...piece,
            x: currentX,
            y: currentY,
            width: originalOrientation.width,
            height: originalOrientation.height,
            rotated: originalOrientation.rotated,
          });

          currentX += originalOrientation.width;
          currentRowHeight = originalOrientation.height;
        } else {
          // Cannot place in original orientation, save for second pass
          unplaced.push(piece);
        }
      }
    });

    // Second Pass: Try to place unplaced pieces with rotation
    unplaced.forEach(piece => {
      const rotatedOrientation = {
        width: piece.height,
        height: piece.width,
        rotated: true,
      };

      // Try placing in current row
      if (
        rotatedOrientation.width <= SHEET_WIDTH - currentX &&
        rotatedOrientation.height <= SHEET_HEIGHT - currentY &&
        rotatedOrientation.width <= SHEET_WIDTH &&
        rotatedOrientation.height <= SHEET_HEIGHT
      ) {
        placed.push({
          ...piece,
          x: currentX,
          y: currentY,
          width: rotatedOrientation.width,
          height: rotatedOrientation.height,
          rotated: rotatedOrientation.rotated,
        });

        currentX += rotatedOrientation.width;
        currentRowHeight = Math.max(currentRowHeight, rotatedOrientation.height);
      } else {
        // Try starting a new row
        currentY += currentRowHeight;
        currentX = 0;
        currentRowHeight = 0;

        if (
          rotatedOrientation.width <= SHEET_WIDTH - currentX &&
          rotatedOrientation.height <= SHEET_HEIGHT - currentY &&
          rotatedOrientation.width <= SHEET_WIDTH &&
          rotatedOrientation.height <= SHEET_HEIGHT
        ) {
          placed.push({
            ...piece,
            x: currentX,
            y: currentY,
            width: rotatedOrientation.width,
            height: rotatedOrientation.height,
            rotated: rotatedOrientation.rotated,
          });

          currentX += rotatedOrientation.width;
          currentRowHeight = rotatedOrientation.height;
        } else {
          // Still cannot place, skip the piece
          console.warn(`Could not place piece ${piece.id} even with rotation`);
        }
      }
    });

    return placed;
  };

  const placedPieces = useMemo(
    () => packPieces(SHEET_WIDTH, SHEET_HEIGHT, items),
    [SHEET_WIDTH, SHEET_HEIGHT, items]
  );

  useEffect(() => {
    if (typeof setLayout === 'function') {
      setLayout(placedPieces);
    }
  }, [placedPieces, setLayout]);

  const pieceCounts = useMemo(() => {
    const counts = {};
    let totalPlaced = 0;
    const sheetsNeededByPiece = {};
    let maxSheetsNeeded = 0;
    let maxPieceName = '';

    versions?.forEach(version => {
      counts[version.name] = {
        expected: Number(version.up) || 0,
        quantity: Number(version.quantity) || 0,
        placed: 0,
      };
    });

    placedPieces.forEach(piece => {
      if (counts[piece.name]) {
        counts[piece.name].placed += 1;
        totalPlaced += 1;
      }
    });

    Object.keys(counts).forEach(name => {
      const placedCount = counts[name].placed;
      const quantityNeeded = counts[name].quantity;
      if (placedCount > 0) {
        sheetsNeededByPiece[name] = Math.ceil(quantityNeeded / placedCount);
      } else {
        sheetsNeededByPiece[name] = Infinity;
      }

      if (sheetsNeededByPiece[name] > maxSheetsNeeded) {
        maxSheetsNeeded = sheetsNeededByPiece[name];
        maxPieceName = name;
      }
    });

    return { counts, totalPlaced, sheetsNeededByPiece, maxSheetsNeeded, maxPieceName };
  }, [placedPieces, versions]);

  useEffect(() => {
    setNeededsheets(pieceCounts?.maxSheetsNeeded);
  }, [pieceCounts, setNeededsheets]);

  return (
    <div>
      <SheetContainer>
        <SheetWrap width={SHEET_WIDTH + 'px'} height={SHEET_HEIGHT + 'px'}>
          {placedPieces.map(piece => (
            <Piece
              key={piece.id}
              pieceId={piece.id}
              isVisible={true}
              height={piece.rotated ? piece.width + 'px' : piece.height + 'px'}
              width={piece.rotated ? piece.height + 'px' : piece.width + 'px'}
              style={{
                left: piece.rotated
                  ? piece.x + (piece.width - piece.height) / 2
                  : piece.x,
                top: piece.rotated
                  ? piece.y + (piece.height - piece.width) / 2
                  : piece.y,
                transform: piece.rotated ? 'rotate(90deg)' : 'none',
                transformOrigin: 'center center',
                position: 'absolute',
                boxSizing: 'border-box',
                backgroundColor: piece.color, // Use the assigned color
                border: '1px solid black',
              }}
            >
              <PieceName>{piece.name}</PieceName>
            </Piece>
          ))}
        </SheetWrap>
      </SheetContainer>

      <div>
        <h3 style={{ fontSize: 'calc(1.2rem + .2vw)' }}>Piece Counts</h3>
        <div>
          {Object.entries(pieceCounts.counts).map(([name, { expected, placed, quantity }]) => (
            <div key={name}>
              <span style={{ fontSize: 'calc(1.2rem + .2vw)', fontWeight: 'bold' }}>
                {name}:
              </span>
              <span
                style={{
                  fontSize: 'calc(1.2rem + .2vw)',
                  fontWeight: 'bold',
                  color: placed !== expected ? 'red' : 'green',
                }}
              >
                {placed} of {expected}
              </span>
              <span style={{ fontSize: 'calc(1.2rem + .2vw)', marginLeft: '1rem' }}>
                (Sheets needed:{' '}
                {isFinite(pieceCounts.sheetsNeededByPiece[name])
                  ? pieceCounts.sheetsNeededByPiece[name]
                  : 0}
                )
              </span>
            </div>
          ))}
          <div>
            <span style={{ fontSize: 'calc(1.2rem + .2vw)', fontWeight: 'bold' }}>
              Total Pieces Placed:{' '}
            </span>
            <span style={{ fontSize: 'calc(1.2rem + .2vw)', fontWeight: 'bold' }}>
              {pieceCounts.totalPlaced}
            </span>
          </div>
          <div>
            <span style={{ fontSize: 'calc(1.2rem + .2vw)', fontWeight: 'bold' }}>
              Total Sheets Needed:{' '}
            </span>
            <span style={{ fontSize: 'calc(1.2rem + .2vw)', fontWeight: 'bold' }}>
              {pieceCounts.maxSheetsNeeded !== Infinity
                ? pieceCounts.maxSheetsNeeded
                : 0}{' '}
              (based on {pieceCounts.maxPieceName})
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sheet;